// Importing Link component from react-router-dom to enable client-side navigation
import { Link } from 'react-router-dom';
// Importing a pre-configured Axios instance for making HTTP requests to the backend API
import axiosClient from '../../../api/axiosClient';
// Importing TypeScript types for Course and User for type safety and code clarity
import type { Course, User } from '../../../types';
import { toast } from 'react-toastify';
import { BookOpen, MessageCircle, Trash2, UserPlus, UserMinus } from 'lucide-react';

// Props interface for the component
interface CourseSectionProps {
  title: string;                   // Section title (e.g., "Available Courses")
  courses: Course[];               // List of courses to display
  emptyMessage: string;            // Message to show if no courses
  user: User | null;               // Current logged-in user
  refreshCourses: () => Promise<void>; // Callback to refresh courses after actions
}
// CourseSection component
function CourseSection({ title, courses, emptyMessage, user, refreshCourses }: CourseSectionProps) {
  // Function to enroll the user in a course
  const handleEnroll = async (courseId: number) => {
  try {
    const response = await axiosClient.post(`/elearning/api/courses/${courseId}/enroll/`, {}, {
      headers: { 'X-CSRFToken': document.cookie.match(/csrftoken=([^;]+)/)?.[1] || '' },
    });
    await refreshCourses();

    if (response.data.status === 'enrolled') {
      toast.success('Successfully enrolled! 🎉'); // green success
    } else {
      toast.info('Already enrolled in this course. ℹ️'); // blue info
    }
  } catch (error: any) {
    console.error('[CourseSection] Enroll error:', error.response?.data || error.message);
    toast.error(error.response?.data?.detail || 'Failed to enroll in the course. ❌'); // red error
  }
};

  // Function to unenroll the user from a course
  const handleUnenroll = async (courseId: number) => {
  try {
    const response = await axiosClient.post(`/elearning/api/courses/${courseId}/unenroll/`, {}, {
      headers: { 'X-CSRFToken': document.cookie.match(/csrftoken=([^;]+)/)?.[1] || '' },
    });
    await refreshCourses();

    if (response.data.status === 'unenrolled') {
      toast.success('Successfully unenrolled. 🟠'); // green success
    } else {
      toast.info('Not enrolled in this course. ℹ️'); // blue info
    }
  } catch (error: any) {
    console.error('[CourseSection] Unenroll error:', error.response?.data || error.message);
    toast.error(error.response?.data?.detail || 'Failed to unenroll from the course. ❌'); // red error
  }
};

  // Function to delete a course (only if user is the teacher)
  const handleDelete = async (courseId: number) => {
  if (window.confirm('Are you sure you want to delete this course?')) {
    try {
      await axiosClient.delete(`/elearning/api/courses/${courseId}/`, {
        headers: { 'X-CSRFToken': document.cookie.match(/csrftoken=([^;]+)/)?.[1] || '' },
      });
      await refreshCourses();
      toast.success('Course deleted successfully. 🗑️'); // green success
    } catch (error: any) {
      console.error('[CourseSection] Delete error:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Failed to delete the course. ❌'); // red error
    }
  }
};
  return (
    <div className="mb-12">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">{title}</h2>
      {courses.length ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map(course => (
            <div
              key={course.id}
              className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition p-6 flex flex-col justify-between"
            >
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{course.name}</h3>
                <p className="text-gray-600 mb-4 line-clamp-3">{course.description}</p>
                <p className="text-gray-700 mb-4 font-medium">
                  Instructor: {course.teacher.real_name || course.teacher.username}
                </p>
                {user && course.materials && (course.enrolled || course.teacher.id === user.id) && (
                  <a
                    href={course.materials}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition"
                  >
                    <BookOpen className="w-4 h-4" /> View Materials
                  </a>
                )}
              </div>
              <div className="flex flex-wrap gap-3 mt-4">
                <Link
                  to={`/courses/${course.id}`}
                  className="flex-1 text-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition"
                >
                  View Course
                </Link>
                {(course.enrolled || (user && course.teacher.id === user.id)) && (
                  <Link
                    to={`/chat/${course.slug}`}
                    className="flex-1 text-center px-4 py-2 bg-green-600 text-white rounded-lg shadow hover:bg-green-700 transition"
                  >
                    <MessageCircle className="w-4 h-4 inline mr-1" /> Chat
                  </Link>
                )}
                {user && course.teacher.id === user.id ? (
                  <button
                    onClick={() => handleDelete(course.id)}
                    className="flex-1 text-center px-4 py-2 bg-red-600 text-white rounded-lg shadow hover:bg-red-700 transition"
                  >
                    <Trash2 className="w-4 h-4 inline mr-1" /> Delete
                  </button>
                ) : course.enrolled ? (
                  <button
                    onClick={() => handleUnenroll(course.id)}
                    className="flex-1 text-center px-4 py-2 bg-red-600 text-white rounded-lg shadow hover:bg-red-700 transition"
                  >
                    <UserMinus className="w-4 h-4 inline mr-1" /> Unenroll
                  </button>
                ) : (
                  <button
                    onClick={() => handleEnroll(course.id)}
                    className="flex-1 text-center px-4 py-2 bg-indigo-600 text-white rounded-lg shadow hover:bg-indigo-700 transition"
                  >
                    <UserPlus className="w-4 h-4 inline mr-1" /> Enroll
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-500">{emptyMessage}</p>
      )}
    </div>
  );
}
export default CourseSection;